"use client"

import React, { useEffect, useState } from "react"
import quizData from "../publicbuildings.json"
import { QuizQuestion } from "./QuizQuestion"
import { QuizResult } from "./QuizResult"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { CheckCircle, XCircle } from "lucide-react"

/** JSONレコードの型定義 */
interface PublicBuildingData {
  公共建築名: string
  建設場所: string
  設計者: string
  概要: string
  タイプ: string
}

/** 回答履歴を保持するための型 */
interface AnswerRecord {
  questionText: string // 問題文(概要)
  userChoiceName: string // ユーザーが選択した公共建築名
  isCorrect: boolean
  correctName: string // 正解(公共建築名)
}

/**
 * PublicQuiz メインコンポーネント
 * (公共建築の事例クイズ)
 */
export default function PublicQuiz() {
  // 問題データをStateに格納(最初にシャッフル)
  const [data, setData] = useState<PublicBuildingData[]>([])
  // 現在の問題インデックス
  const [currentIndex, setCurrentIndex] = useState(0)
  // 正解数
  const [score, setScore] = useState(0)
  // 全問終了フラグ
  const [isQuizFinished, setIsQuizFinished] = useState(false)

  // 現在問題が回答済みか & 正解だったか
  const [isAnswered, setIsAnswered] = useState(false)
  const [isCorrect, setIsCorrect] = useState(false)

  // 表示中の4択(= HouseData配列)
  const [currentChoices, setCurrentChoices] = useState<PublicBuildingData[]>([])

  // ユーザーの回答履歴
  const [userAnswers, setUserAnswers] = useState<AnswerRecord[]>([])

  /**
   * マウント時に quizData をシャッフルしてStateにセット
   */
  useEffect(() => {
    const shuffled = [...quizData]
    shuffleArray(shuffled)
    setData(shuffled)
  }, [])

  /**
   * 4択が生成された際に受け取る
   */
  const handleChoicesGenerated = (choices: PublicBuildingData[]) => {
    setCurrentChoices(choices)
  }

  /**
   * 回答を選択
   */
  const handleChoice = (choiceName: string) => {
    if (!isAnswered && data[currentIndex]) {
      const correctName = data[currentIndex].公共建築名
      const correct = choiceName === correctName
      if (correct) setScore((prev) => prev + 1)

      setIsAnswered(true)
      setIsCorrect(correct)

      // 回答履歴を追加
      setUserAnswers((prev) => [
        ...prev,
        {
          questionText: data[currentIndex].概要,
          userChoiceName: choiceName,
          isCorrect: correct,
          correctName: correctName,
        },
      ])
    }
  }

  /**
   * 次の問題へ or 終了
   */
  const handleNextQuestion = () => {
    if (currentIndex < data.length - 1) {
      setCurrentIndex((prev) => prev + 1)
      setIsAnswered(false)
      setIsCorrect(false)
      setCurrentChoices([])
    } else {
      setIsQuizFinished(true)
    }
  }

  /**
   * 最初からやり直す
   * (再度シャッフルするなど)
   */
  const handleReset = () => {
    const shuffled = [...quizData]
    shuffleArray(shuffled)
    setData(shuffled)

    setCurrentIndex(0)
    setScore(0)
    setIsQuizFinished(false)
    setIsAnswered(false)
    setIsCorrect(false)
    setCurrentChoices([])
    setUserAnswers([])
  }

  // データがまだない(ロード前)場合
  if (data.length === 0) {
    return <p>読み込み中...</p>
  }

  // 結果画面
  if (isQuizFinished) {
    return (
      <Card className="max-w-xl mx-auto mt-8">
        <CardHeader>
          <CardTitle>クイズ結果</CardTitle>
        </CardHeader>
        <CardContent>
          <QuizResult
            score={score}
            total={data.length}
            userAnswers={userAnswers}
            onReset={handleReset}
          />
        </CardContent>
      </Card>
    )
  }

  // 現在の問題
  const currentQuestion = data[currentIndex]

  return (
    <div className="max-w-xl mx-auto my-8">
      <Card>
        <CardHeader>
          <CardTitle>問題</CardTitle>
          {currentIndex + 1} / {data.length}
        </CardHeader>
        <CardContent>
          {/* 個別問題コンポーネント */}
          <QuizQuestion
            buildingData={currentQuestion}
            allBuildings={data}
            onChoicesGenerated={handleChoicesGenerated}
            onChoice={handleChoice}
            isAnswered={isAnswered}
          />

          {isAnswered && (
            <div className="mt-4">
              {isCorrect ? (
                <div className="flex items-center gap-1 text-green-600 font-semibold">
                  <CheckCircle className="w-5 h-5" />
                  <span>正解です！</span>
                </div>
              ) : (
                <div className="flex flex-col gap-1 text-red-600 font-semibold">
                  <div className="flex items-center gap-1">
                    <XCircle className="w-5 h-5" />
                    <span>不正解です…</span>
                  </div>
                  <p className="text-base mt-2">
                    正解は{" "}
                    <span className="font-bold text-green-700">
                      {currentQuestion.公共建築名}
                    </span>{" "}
                    でした。
                  </p>
                </div>
              )}

              {/* 4択の情報をテーブル表示 */}
              <div className="mt-4 overflow-x-auto">
                <table className="w-full border border-collapse text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border px-2 py-1">公共建築名</th>
                      <th className="border px-2 py-1">建設場所</th>
                      <th className="border px-2 py-1">設計者</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentChoices.map((ch, idx) => (
                      <tr key={idx}>
                        <td className="border px-2 py-1">{ch.公共建築名}</td>
                        <td className="border px-2 py-1">{ch.建設場所}</td>
                        <td className="border px-2 py-1">{ch.設計者}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Button className="mt-4" onClick={handleNextQuestion}>
                次へ
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="mt-4 text-center">
        <Button variant="secondary" onClick={handleReset}>
          最初からやり直す
        </Button>
      </div>
    </div>
  )
}

/** シャッフル関数 */
function shuffleArray<T>(arr: T[]) {
  for (let i = arr.length - 1; i > 0; i--) {
    const r = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[r]] = [arr[r], arr[i]]
  }
}
